import {
  type FocusEvent,
  type ForwardedRef,
  type MouseEvent,
  forwardRef,
  useRef,
} from "react";
import { cn, Input, type InputProps } from "ui";

export const BaseInput = forwardRef(function BaseInput(
  {
    className,
    disabled,
    onFocus,
    onMouseDown,
    onMouseUp,
    ...props
  }: InputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  const isFocusingClickRef = useRef(false);

  // Selects the value on focus, so typing overwrites it like in a spreadsheet.
  function handleFocus(event: FocusEvent<HTMLInputElement>) {
    event.currentTarget.select();
    onFocus?.(event);
  }

  function handleMouseDown(event: MouseEvent<HTMLInputElement>) {
    isFocusingClickRef.current = document.activeElement !== event.currentTarget;
    onMouseDown?.(event);
  }

  // The mouseup of the focusing click would place the caret and drop the
  // selection. Later clicks place the caret as usual.
  function handleMouseUp(event: MouseEvent<HTMLInputElement>) {
    if (isFocusingClickRef.current) {
      event.preventDefault();
      isFocusingClickRef.current = false;
    }

    onMouseUp?.(event);
  }

  return (
    <Input
      disabled={disabled}
      ref={ref}
      className={cn(
        "p-1 h-8 w-14 text-center rounded-sm md:text-xs text-xs",
        className,
      )}
      onFocus={handleFocus}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...props}
    />
  );
});
